/** Конфигурация нативного ТСД (Android-приложение `android/`).
 *
 *  Статика терминала открыта в WebView из локальной копии, а рабочие данные
 *  идут не в ritmflow, а на сторонний сервер данных в локальной сети
 *  (`http://<адрес>:<порт>/rbs/rfv1/`). Адрес и короткоживущий ключ к нему приложение
 *  получает из ritmflow (`/api/devices/native/bootstrap/`) и отдаёт через мост
 *  `window.NativeApp.getConfig()` — JSON-строкой. Ключ приложение обновляет
 *  само, поэтому читаем конфиг на каждый запрос (вызов моста синхронный и
 *  дешёвый).
 *
 *  В обычном браузере моста нет — `getNativeConfig()` возвращает null, и
 *  терминал работает с ritmflow как раньше.
 */

export interface NativeConfig {
  /** Адрес API сервера данных, всегда с завершающим `/` (…/rbs/rfv1/). */
  dataApiBase: string
  /** Ключ терминала к серверу данных. */
  dataKey: string
  /** Заголовок, в котором передаётся ключ. */
  keyHeader: string
  deviceLabel?: string
  locationName?: string
  /** Режим отладки — задаётся в ritmflow у терминала (`Device.debug_mode`). */
  debugMode?: boolean
}

interface NativeBridge {
  getConfig?: () => string
}

function bridge(): NativeBridge | undefined {
  return (window as unknown as { NativeApp?: NativeBridge }).NativeApp
}

export function getNativeConfig(): NativeConfig | null {
  const raw = bridge()?.getConfig?.()
  if (!raw) return null
  try {
    const c = JSON.parse(raw) as Partial<NativeConfig>
    if (!c.dataApiBase || !c.dataKey) return null
    const base = c.dataApiBase.endsWith('/') ? c.dataApiBase : `${c.dataApiBase}/`
    return { ...c, dataApiBase: base, keyHeader: c.keyHeader || 'X-Terminal-Key' } as NativeConfig
  } catch {
    return null
  }
}

/** Ключ для запроса на сервер данных; null — URL не на сервер данных. */
export function dataServerKeyFor(url: string): { header: string; key: string } | null {
  const c = getNativeConfig()
  if (!c || !url.startsWith(c.dataApiBase)) return null
  return { header: c.keyHeader, key: c.dataKey }
}
